<template>
  <div class="container--reading">
    <!-- 阅读进度条 -->
    <div class="reading-progress" :style="{ width: progress + '%' }"></div>

    <!-- 面包屑导航 -->
    <div class="kp-breadcrumb">
      <a class="back-link" @click="goHome"><i class="ri-arrow-left-s-line"></i> 返回列表</a>
    </div>

    <!-- 日期导航 -->
    <div class="date-nav" v-if="nearbyDates.length">
      <a
        v-for="d in nearbyDates"
        :key="d"
        class="date-pill"
        :class="{ active: d === pushDate }"
        @click="goToDate(d)"
      >{{ formatDateShort(d) }}</a>
    </div>

    <!-- 加载/空/错误态 -->
    <div v-if="loading" class="loading">加载中…</div>
    <div v-else-if="error" class="error-tip"><i class="ri-error-warning-line"></i> {{ error }}</div>
    <div v-else-if="!article" class="empty"><i class="ri-inbox-line"></i> 该日期暂无内容</div>

    <!-- 文章卡片 -->
    <article v-else class="kp-article card spotlight">
      <h1 class="kp-title">{{ article.title }}</h1>

      <!-- 标签 pill（点击跳列表页按标签筛选） -->
      <div v-if="article.tags && article.tags.length" class="kp-tags">
        <a
          v-for="t in article.tags"
          :key="t"
          class="about-tag kp-tag"
          @click="goTag(t)"
        ><i class="ri-hashtag"></i>{{ t }}</a>
      </div>

      <!-- 文章 meta -->
      <div class="kp-meta">
        <span v-if="article.topic_type" class="kp-meta-item"><i class="ri-price-tag-3-line"></i> {{ article.topic_type }}</span>
        <span class="kp-meta-item"><i class="ri-time-line"></i> {{ readMinutes }} 分钟</span>
        <span class="kp-meta-item"><i class="ri-file-text-line"></i> {{ wordCount }} 字</span>
        <span class="kp-meta-item"><i class="ri-eye-line"></i> {{ viewCount }} 次阅读</span>
        <a
          class="kp-meta-item kp-share-btn"
          :class="{ 'kp-fav-active': isFav }"
          @click="toggleFav"
          :title="isFav ? '取消收藏' : '收藏（保存在本地浏览器）'"
        >
          <i :class="isFav ? 'ri-star-fill' : 'ri-star-line'"></i> {{ isFav ? '已收藏' : '收藏' }}
        </a>
        <a class="kp-meta-item kp-share-btn" @click="shareVisible = true" title="生成分享卡片">
          <i class="ri-share-line"></i> 分享
        </a>
      </div>

      <!-- 文章目录（可折叠） -->
      <div v-if="toc.length" class="kp-toc">
        <div class="kp-toc-head" @click="tocOpen = !tocOpen">
          <i class="ri-file-list-3-line"></i>
          <span>目录</span>
          <span class="kp-toc-toggle">{{ tocOpen ? '收起' : '展开' }}</span>
          <i class="ri-arrow-down-s-line kp-toc-arrow" :class="{ open: tocOpen }"></i>
        </div>
        <div v-show="tocOpen" class="kp-toc-body">
          <a
            v-for="t in toc"
            :key="t.id"
            class="kp-toc-item"
            :class="'lv-' + t.level"
            @click="scrollToHeading(t.id)"
          >{{ t.text }}</a>
        </div>
      </div>

      <!-- Markdown 正文 -->
      <div class="md-content">
        <div v-if="article.core_concept" v-html="renderMd(article.core_concept)"></div>
        <div v-html="renderMd(article.content)"></div>
        <div v-if="article.pitfalls" v-html="renderMd(article.pitfalls)"></div>
        <div v-if="article.summary" v-html="renderMd('### 一句话总结\n\n' + article.summary)"></div>
      </div>

      <hr class="md-hr" />
      <p v-if="article.topic_type" class="kp-footer-meta">
        <em>主题：{{ article.topic_type }}</em>
      </p>
    </article>

    <!-- 相关笔记推荐 -->
    <div v-if="relatedList.length" class="kp-related">
      <div class="kp-related-title"><i class="ri-links-line"></i> 相关笔记</div>
      <a
        v-for="r in relatedList"
        :key="r.push_date"
        class="kp-related-item"
        @click="goToDate(r.push_date)"
      >
        <span class="kp-related-item-title">{{ r.title }}</span>
        <span class="kp-related-item-meta">{{ formatDateShort(r.push_date) }} · {{ r.view_count || 0 }} 阅读</span>
      </a>
    </div>

    <!-- 上一篇 / 下一篇导航 -->
    <div class="kp-pager" v-if="article && (prevDate || nextDate)">
      <a
        class="kp-pager-btn"
        :class="{ disabled: !prevDate }"
        @click="prevDate && goToDate(prevDate)"
      ><i class="ri-arrow-left-s-line"></i> 上一篇<span v-if="prevDate" class="kp-pager-date">{{ formatDateShort(prevDate) }}</span></a>
      <a
        class="kp-pager-btn right"
        :class="{ disabled: !nextDate }"
        @click="nextDate && goToDate(nextDate)"
      >下一篇 <i class="ri-arrow-right-s-line"></i><span v-if="nextDate" class="kp-pager-date">{{ formatDateShort(nextDate) }}</span></a>
    </div>

    <!-- 底部导航 -->
    <div class="kp-nav-bottom" v-if="article">
      <a class="back-link" @click="goHome"><i class="ri-arrow-left-s-line"></i> 返回首页</a>
    </div>

    <!-- 阅读伴侣机器人 -->
    <div v-if="article" class="reading-buddy" :class="{ collapsed: buddyCollapsed }">
      <div class="buddy-bubble" v-show="!buddyCollapsed && currentReading">
        <div class="buddy-bubble-label">正在阅读</div>
        <div class="buddy-bubble-text">{{ currentReading }}</div>
      </div>
      <div class="buddy-float-outer">
        <div class="buddy-robot" @click="buddyCollapsed = !buddyCollapsed">
          <div class="buddy-antenna"></div>
          <div class="buddy-head">
            <div class="buddy-eye buddy-eye-l"></div>
            <div class="buddy-eye buddy-eye-r"></div>
          </div>
          <div class="buddy-body">
            <div class="buddy-chest"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer>
      <p>AI 学习笔记 · 个人学习总结</p>
      <div class="beian">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">闽ICP备2026026930号-1</a>
        <br>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=35010202002243" target="_blank" rel="noopener noreferrer">
          <img src="/static/beian-logo.png" style="vertical-align:middle;width:16px;height:16px;margin-right:4px;" alt="公安备案标识" />
          闽公网安备35010202002243号
        </a>
      </div>
    </footer>

    <!-- 分享卡片弹层 -->
    <ShareCard
      :visible="shareVisible"
      :title="article ? article.title : ''"
      :push-date="pushDate"
      :topic-type="article ? (article.topic_type || '') : ''"
      @close="shareVisible = false"
    />
  </div>
</template>

<script>
import aiPush from '../api/aiPush'
import ShareCard from '../components/ShareCard.vue'
import fav from '../utils/favorites'
import review from '../utils/review'

/**
 * 极简 markdown 渲染（保持与现有 knowledge_*.html 视觉一致）
 * 支持：##/###/#### 标题、代码块```、行内`code`、表格、列表、引用、分隔线、链接、加粗
 * 不引入外部 markdown 库，保持依赖最小。
 */
function escapeHtml (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// 标题锚点 id 计数器（每篇文章渲染前由 buildToc 重置为 0）
let headingSeq = 0

function renderMarkdown (md) {
  if (!md) return ''
  const lines = String(md).split(/\r?\n/)
  let html = ''
  let i = 0
  let inUl = false
  let inOl = false
  let inTable = false

  const closeLists = () => {
    if (inUl) { html += '</ul>'; inUl = false }
    if (inOl) { html += '</ol>'; inOl = false }
  }
  const closeTable = () => {
    if (inTable) { html += '</tbody></table>'; inTable = false }
  }

  while (i < lines.length) {
    let line = lines[i]

    // 代码块
    const fence = line.match(/^```(\w*)/)
    if (fence) {
      closeLists(); closeTable()
      const lang = fence[1] || ''
      let code = ''
      i++
      while (i < lines.length && !/^```/.test(lines[i])) {
        code += (code ? '\n' : '') + escapeHtml(lines[i])
        i++
      }
      i++ // 跳过闭合 ```
      html += '<pre class="code-block" data-lang="' + lang + '"><code>' + code + '</code></pre>'
      continue
    }

    // 空行
    if (/^\s*$/.test(line)) {
      closeLists(); closeTable()
      i++
      continue
    }

    // 分隔线
    if (/^---+\s*$/.test(line) || /^\*\*\*+\s*$/.test(line)) {
      closeLists(); closeTable()
      html += '<hr class="md-hr" />'
      i++
      continue
    }

    // 标题（生成锚点 id 供目录跳转）
    const h1 = line.match(/^#\s+(.*)$/)
    const h2 = line.match(/^##\s+(.*)$/)
    const h3 = line.match(/^###\s+(.*)$/)
    const h4 = line.match(/^####\s+(.*)$/)
    if (h1) { closeLists(); closeTable(); html += '<h1 class="md-h1">' + inline(h1[1]) + '</h1>'; i++; continue }
    if (h2) { closeLists(); closeTable(); html += '<h2 id="md-h-' + (headingSeq++) + '" class="md-h2">' + inline(h2[1]) + '</h2>'; i++; continue }
    if (h3) { closeLists(); closeTable(); html += '<h3 id="md-h-' + (headingSeq++) + '" class="md-h3">' + inline(h3[1]) + '</h3>'; i++; continue }
    if (h4) { closeLists(); closeTable(); html += '<h4 class="md-h4">' + inline(h4[1]) + '</h4>'; i++; continue }

    // 引用
    const quote = line.match(/^>\s?(.*)$/)
    if (quote) {
      closeLists(); closeTable()
      html += '<blockquote class="md-quote"><p>' + inline(quote[1]) + '</p></blockquote>'
      i++
      continue
    }

    // 表格（| a | b |）
    if (/^\|.*\|\s*$/.test(line)) {
      closeLists()
      // 分隔行 |---|---| - 跳过
      if (/^\|[\s:-]+\|/.test(line)) {
        i++
        continue
      }
      if (!inTable) {
        // 首行是表头
        html += '<table class="md-table"><thead><tr>'
        line.split('|').slice(1, -1).forEach(h => { html += '<th>' + inline(h.trim()) + '</th>' })
        html += '</tr></thead><tbody>'
        inTable = true
        i++
        continue
      }
      // 数据行
      html += '<tr>'
      line.split('|').slice(1, -1).forEach(c => { html += '<td>' + inline(c.trim()) + '</td>' })
      html += '</tr>'
      i++
      continue
    } else if (inTable) {
      closeTable()
    }

    // 无序列表
    if (/^\s*[-*+]\s+/.test(line)) {
      if (inOl) { html += '</ol>'; inOl = false }
      if (!inUl) { html += '<ul class="md-ul">'; inUl = true }
      html += '<li>' + inline(line.replace(/^\s*[-*+]\s+/, '')) + '</li>'
      i++
      continue
    }
    // 有序列表
    if (/^\s*\d+\.\s+/.test(line)) {
      if (inUl) { html += '</ul>'; inUl = false }
      if (!inOl) { html += '<ol class="md-ol">'; inOl = true }
      html += '<li>' + inline(line.replace(/^\s*\d+\.\s+/, '')) + '</li>'
      i++
      continue
    }

    // 普通段落
    closeLists(); closeTable()
    html += '<p>' + inline(line) + '</p>'
    i++
  }
  closeLists(); closeTable()
  return html
}

function inline (s) {
  s = escapeHtml(s)
  // 行内代码 `xxx` —— 最先处理，保护内部内容不被其他规则解析
  const codeSlots = []
  s = s.replace(/`([^`]+)`/g, (_, code) => {
    codeSlots.push('<code class="inline-code">' + code + '</code>')
    return '%%CODE_' + (codeSlots.length - 1) + '%%'
  })
  // 加粗 **xxx**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // 斜体 *xxx*（不匹配 **）
  s = s.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
  // 删除线 ~~xxx~~
  s = s.replace(/~~([^~]+)~~/g, '<del>$1</del>')
  // 链接 [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="md-link" href="$2" target="_blank" rel="noopener">$1</a>')
  // 图片 ![alt](url)
  s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;border-radius:8px;margin:8px 0;" />')
  // 还原行内代码
  s = s.replace(/%%CODE_(\d+)%%/g, (_, idx) => codeSlots[Number(idx)])
  return s
}

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export default {
  name: 'KnowledgePage',
  components: { ShareCard },
  props: { pushDate: { type: String, required: true } },
  data () {
    return {
      loading: false,
      error: '',
      article: null,
      nearbyDates: [], // 日期导航 pill（前后几天）
      allDates: [],    // 已发布日期全量（上一篇/下一篇导航用）
      toc: [],         // 文章目录（h2/h3 标题）
      tocOpen: false,  // 目录默认折叠
      progress: 0,     // 阅读进度 0-100
      buddyCollapsed: false,
      currentReading: '',
      viewCount: 0,    // 实时阅读量（incrView 返回值）
      relatedList: [], // 相关笔记推荐
      shareVisible: false  // 分享卡片弹层
    }
  },
  computed: {
    /** 全部正文拼接后的字数（去 markdown 标记） */
    wordCount () {
      if (!this.article) return 0
      const plain = [this.article.core_concept, this.article.content, this.article.pitfalls, this.article.summary]
        .filter(Boolean)
        .join('\n')
        .replace(/```[\s\S]*?```/g, '')
        .replace(/[#*_>`~\-|[\]()]/g, '')
        .replace(/\s+/g, '')
      return plain.length
    },
    /** 预计阅读时长（按 300 字/分钟） */
    readMinutes () {
      return Math.max(1, Math.round(this.wordCount / 300))
    },
    /** 上一篇（allDates 倒序，idx+1 为更早一天） */
    prevDate () {
      const idx = this.allDates.indexOf(this.pushDate)
      return idx >= 0 && idx < this.allDates.length - 1 ? this.allDates[idx + 1] : ''
    },
    /** 下一篇（idx-1 为更新一天） */
    nextDate () {
      const idx = this.allDates.indexOf(this.pushDate)
      return idx > 0 ? this.allDates[idx - 1] : ''
    },
    /** 当前篇是否已收藏（响应 favState 变化） */
    isFav () {
      return fav.favState.items.some(it => it.push_date === this.pushDate)
    }
  },
  watch: {
    pushDate () { this.load() }
  },
  mounted () {
    this.load()
    window.addEventListener('scroll', this.onScroll)
  },
  beforeUnmount () {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    async load () {
      if (!this.pushDate) return
      this.loading = true
      this.error = ''
      this.article = null
      this.relatedList = []
      this.viewCount = 0
      try {
        const { data } = await aiPush.getByDate(this.pushDate)
        this.article = data
        this.viewCount = (data && data.view_count) || 0
        this.buildToc()
        this.progress = 0
        // 标记已读（复习提醒数据源）
        if (data) {
          review.markRead({ push_date: this.pushDate, title: data.title, topic_type: data.topic_type })
        }
        await this.loadNearbyDates()
        // 阅读量 +1（并行，失败静默）；返回实时总数直接刷新显示
        aiPush.incrView(this.pushDate)
          .then(({ data }) => { if (data >= 0) this.viewCount = data })
          .catch(() => {})
        // 相关笔记推荐（并行，失败静默）
        aiPush.related(this.pushDate, 5)
          .then(({ data }) => { this.relatedList = data || [] })
          .catch(() => {})
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    /** 提取目录（与 renderMarkdown 使用相同的锚点 id 顺序） */
    buildToc () {
      if (!this.article) { this.toc = []; return }
      headingSeq = 0
      const list = []
      const scan = (md) => {
        if (!md) return
        for (const line of String(md).split(/\r?\n/)) {
          const m = line.match(/^(#{2,3})\s+(.*)$/)
          if (m) {
            const text = m[2]
              .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 去链接，留文字
              .replace(/[*`]/g, '')
              .trim()
            list.push({ level: m[1].length, text, id: 'md-h-' + (headingSeq++) })
          }
        }
      }
      scan(this.article.core_concept)
      scan(this.article.content)
      scan(this.article.pitfalls)
      scan(this.article.summary)
      this.toc = list
    },
    onScroll () {
      const doc = document.documentElement
      const total = doc.scrollHeight - window.innerHeight
      this.progress = total > 0 ? Math.min(100, Math.round((window.scrollY / total) * 100)) : 0

      // 提取当前阅读位置的内容
      this.updateCurrentReading()
    },
    updateCurrentReading () {
      const headings = document.querySelectorAll('.md-content h1, .md-content h2, .md-content h3, .md-content p')
      if (!headings.length) {
        this.currentReading = ''
        return
      }

      const viewportMiddle = window.innerHeight / 2
      let closest = null
      let closestDist = Infinity

      headings.forEach(el => {
        const rect = el.getBoundingClientRect()
        const dist = Math.abs(rect.top - viewportMiddle)
        if (dist < closestDist) {
          closestDist = dist
          closest = el
        }
      })

      if (closest) {
        let text = closest.textContent.trim()
        // 如果是标题，取标题文本；如果是段落，截取前 40 字
        if (closest.tagName.match(/^H[1-3]$/)) {
          this.currentReading = text
        } else {
          this.currentReading = text.length > 40 ? text.slice(0, 40) + '...' : text
        }
      }
    },
    scrollToHeading (id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    async loadNearbyDates () {
      try {
        const { data } = await aiPush.dateList(60)
        const all = data || []
        this.allDates = all
        // 当前日期前后各取几个，保持与原 kp-pill 体验一致
        const idx = all.indexOf(this.pushDate)
        if (idx >= 0) {
          this.nearbyDates = all.slice(Math.max(0, idx - 3), idx + 4)
        } else {
          this.nearbyDates = all.slice(0, 7)
        }
      } catch (e) {
        this.nearbyDates = []
        this.allDates = []
      }
    },
    renderMd (md) { return renderMarkdown(md) },
    formatDateCN (d) {
      if (!d) return ''
      const dt = new Date(d.replace(/-/g, '/'))
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      return `${m}-${day} ${WEEKDAYS[dt.getDay()]}`
    },
    formatDateShort (d) {
      if (!d) return ''
      const dt = new Date(d.replace(/-/g, '/'))
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      return `${m}-${day} ${WEEKDAYS[dt.getDay()]}`
    },
    goHome () { window.location.hash = '#/' },
    goTag (t) {
      window.location.hash = '#/notes/tag/' + encodeURIComponent(t)
    },
    toggleFav () {
      if (!this.article) return
      fav.toggle({
        push_date: this.pushDate,
        title: this.article.title,
        topic_type: this.article.topic_type
      })
    },
    goToDate (d) {
      if (d === this.pushDate) return
      window.location.hash = `#/date/${d}`
    }
  }
}
</script>
